import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, query, orderBy, getDocs, addDoc, deleteDoc, doc, limit, serverTimestamp } from 'firebase/firestore';
import { FaTrash, FaPrint, FaList, FaTimes, FaPlus, FaBuilding, FaFileInvoice, FaTools, FaSave, FaUserTie, FaHistory, FaEdit } from 'react-icons/fa';

const ShipmentInvoice = () => {
    
    // --- States ---
    const [companies, setCompanies] = useState([]); 
    const [savedBuyers, setSavedBuyers] = useState([]);
    const [savedSenders, setSavedSenders] = useState([]);
    const [senderMode, setSenderMode] = useState('internal'); 
    const [selectedCompanyId, setSelectedCompanyId] = useState('');
    const [showBuyerDropdown, setShowBuyerDropdown] = useState(false);

    // --- ✅ Firebase Recent History State ---
    const [recentInvoices, setRecentInvoices] = useState([]);
    const [showAllRecent, setShowAllRecent] = useState(false);
    
    const [senderInfo, setSenderInfo] = useState({ name: '', address: '' });
    const today = new Date().toLocaleDateString('en-GB').replace(/\//g, '.');
    const [receiver, setReceiver] = useState({
        date: today, 
        toCompany: '',
        address: '',
        attn: '',
        phone: '',
        vat: '',
        eori: '',
        tax: ''
    });

    const [items, setItems] = useState([
        { origin: 'BANGLADESH', fabric: '100% Cotton', quantity: '', description: '', hsCode: '', unitPrice: '', total: 0 }
    ]);

    const [footerInfo, setFooterInfo] = useState({
        contactPerson: 'CONTACT PERSON', 
        mobile: '+8801750-021581',
        email: 'aronnoanis4u@gmail.com'
    });

    const productSuggestions = ["Cotton T-Shirt", "Polo Shirt", "Sweat Hoodie", "Sweatshirt", "Mens Sweater", "Womens Sweater", "Knit Shorts", "Mens Trouser", "Womens Leggings", "Shirt", "Formal Shirt", "Denim Jeans", "Jogger Pant", "Track Pant", "Nightwear Set", "Pajama Set", "Tank Top", "Vest", "Ladies Dress", "Mens Jacket", "Ladies Jacket", "Blazer", "Kids T-Shirt", "Kids Hoodie", "Kids Shorts", "Sports T-Shirt", "Oversized T-Shirt", "Crop Top", "Long Sleeve T-Shirt", "Thermal Wear", "Tote Bag", "Jute Bag", "Cap", "Swatch Card", "Lab Dip"];
    const fabricSuggestions = ["100% Cotton", "95% Cotton 5% Elastane", "Polyester", "Denim", "Viscose", "Jute", "Silk", "Linen"];
    const hsCodeMap = {"cotton t-shirt": "6109.10", "polo shirt": "6105.10", "sweat hoodie": "6110.20", "sweatshirt": "6110.20", "mens sweater": "6110.10", "womens sweater": "6110.10", "knit shorts": "6103.43", "mens trouser": "6203.42", "womens leggings": "6104.63", "shirt": "6205.20", "formal shirt": "6205.20", "denim jeans": "6203.42", "jogger pant": "6103.42", "track pant": "6103.43", "nightwear set": "6107.21", "pajama set": "6107.21", "tank top": "6109.10", "vest": "6109.10", "ladies dress": "6204.42", "mens jacket": "6201.93", "ladies jacket": "6202.93", "blazer": "6203.33", "kids t-shirt": "6111.20", "kids hoodie": "6111.20", "kids shorts": "6111.20", "sports t-shirt": "6109.90", "oversized t-shirt": "6109.10", "crop top": "6114.30", "long sleeve t-shirt": "6109.10", "thermal wear": "6107.11", "t-shirt": "6109.10", "polo": "6105.10", "hoodie": "6110.30", "pant": "6203.42", "shorts": "6203.42", "tote bag": "4202.92", "jute bag": "6305.10", "cap": "6505.00", "swatch card": "6307.90", "lab dip": "6307.90"};

    // --- Fetch Data ---
    useEffect(() => {
        fetchInitialData();
        fetchRecentInvoices();
    }, []);

    const fetchInitialData = async () => {
        const qComp = query(collection(db, "companies"), orderBy("name"));
        const snapComp = await getDocs(qComp);
        setCompanies(snapComp.docs.map(doc => ({ id: doc.id, ...doc.data() })));

        const qBuyers = query(collection(db, "shipment_buyers"), orderBy("name"));
        const snapBuyers = await getDocs(qBuyers);
        setSavedBuyers(snapBuyers.docs.map(doc => ({ id: doc.id, ...doc.data() })));

        const qSenders = query(collection(db, "shipment_senders"), orderBy("name"));
        const snapSenders = await getDocs(qSenders);
        setSavedSenders(snapSenders.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    };

    const fetchRecentInvoices = async () => {
        const qRecent = query(collection(db, "shipment_recent_history"), orderBy("createdAt", "desc"), limit(20));
        const snapRecent = await getDocs(qRecent);
        setRecentInvoices(snapRecent.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    };

    const handleCompanySelect = (e) => {
        const compId = e.target.value;
        setSelectedCompanyId(compId);
        const comp = companies.find(c => c.id === compId);
        if (comp) {
            setSenderInfo({ name: comp.name, address: comp.address });
            setFooterInfo(prev => ({ ...prev, contactPerson: comp.customerName || 'CONTACT PERSON' }));
        }
    };

    const handleExternalSenderSelect = (e) => {
        const senderId = e.target.value;
        if (!senderId) return;
        const sender = savedSenders.find(s => s.id === senderId);
        if (sender) {
            setSenderInfo({ name: sender.name, address: sender.address });
            setFooterInfo({ contactPerson: sender.contactPerson || 'CONTACT PERSON', mobile: sender.mobile || '', email: sender.email || '' });
        }
    };

    const handleSaveExternalSender = async () => {
        if (!senderInfo.name || !senderInfo.address) { alert("Please enter Company Name and Address."); return; }
        const newSender = { name: senderInfo.name, address: senderInfo.address, contactPerson: footerInfo.contactPerson, mobile: footerInfo.mobile, email: footerInfo.email };
        try {
            const docRef = await addDoc(collection(db, "shipment_senders"), newSender);
            setSavedSenders([...savedSenders, { id: docRef.id, ...newSender }]);
            alert("External Sender Saved Successfully!");
        } catch (error) { console.error(error); }
    };

    const handleDeleteSender = async (id, name) => {
        if (window.confirm(`Delete sender "${name}"?`)) {
            try { await deleteDoc(doc(db, "shipment_senders", id)); setSavedSenders(savedSenders.filter(s => s.id !== id)); } catch (error) { console.error(error); }
        }
    };

    const selectBuyer = (buyer) => {
        setReceiver({ ...receiver, toCompany: buyer.name, address: buyer.address || '', phone: buyer.phone || '', attn: buyer.attn || '', vat: buyer.vat || '', eori: buyer.eori || '', tax: buyer.tax || '' });
        setShowBuyerDropdown(false);
    };

    const handleDeleteBuyer = async (id, name) => {
        if (window.confirm(`Remove "${name}" from saved list?`)) {
            try { await deleteDoc(doc(db, "shipment_buyers", id)); setSavedBuyers(savedBuyers.filter(b => b.id !== id)); } catch (error) { console.error(error); }
        }
    };

    const handleKeyDown = (e, rowIndex, colIndex) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const nextRowInput = document.querySelector(`[data-row="${rowIndex + 1}"][data-col="${colIndex}"]`);
            if (nextRowInput) nextRowInput.focus();
        } else if (e.key === 'Tab' && !e.shiftKey && colIndex === 5) {
            const nextRowFirstInput = document.querySelector(`[data-row="${rowIndex + 1}"][data-col="1"]`);
            if (nextRowFirstInput) { e.preventDefault(); nextRowFirstInput.focus(); }
        }
    };

    const handleItemChange = (index, field, value) => {
        const newItems = [...items];
        newItems[index][field] = value;
        if (field === 'description') {
            const lowerDesc = value.toLowerCase();
            let found = false;
            for (const key in hsCodeMap) {
                if (lowerDesc.includes(key)) { newItems[index]['hsCode'] = hsCodeMap[key]; found = true; break; }
            }
            if (!found && value.trim() !== "") { newItems[index]['hsCode'] = ""; }
        }
        if (field === 'quantity' || field === 'unitPrice') {
            const qtyMatch = newItems[index].quantity.toString().match(/(\d+(\.\d+)?)/);
            const qty = qtyMatch ? parseFloat(qtyMatch[0]) : 0;
            const price = parseFloat(newItems[index].unitPrice) || 0;
            newItems[index].total = qty * price;
        }
        setItems(newItems);
    };

    const addItem = () => setItems([...items, { origin: 'BANGLADESH', fabric: '100% Cotton', quantity: '', description: '', hsCode: '', unitPrice: '', total: 0 }]);
    const removeItem = (index) => setItems(items.filter((_, i) => i !== index));

    // --- ✅ Firebase Save on Print ---
    const handlePrint = async () => {
        if (receiver.toCompany.trim()) {
            const existing = savedBuyers.find(b => b.name.toLowerCase() === receiver.toCompany.trim().toLowerCase());
            if (!existing) {
                try {
                    const newBuyer = { name: receiver.toCompany, address: receiver.address, phone: receiver.phone, attn: receiver.attn, vat: receiver.vat, eori: receiver.eori, tax: receiver.tax };
                    await addDoc(collection(db, "shipment_buyers"), newBuyer);
                    fetchInitialData();
                } catch (e) { console.error(e); }
            }
        }

        const descriptions = items.map(i => i.description).filter(d => d !== '').slice(0, 3).join(", ");
        const newRecord = {
            createdAt: serverTimestamp(),
            timestamp: new Date().toLocaleString(),
            summary: descriptions || "No Description",
            receiver: { ...receiver },
            sender: { ...senderInfo },
            footer: { ...footerInfo },
            items: [...items],
            senderMode: senderMode
        };

        try {
            await addDoc(collection(db, "shipment_recent_history"), newRecord);
            fetchRecentInvoices(); 
        } catch (e) { console.error(e); }

        window.print();
    };

    const handleDeleteHistory = async (id, e) => {
        if (e) e.stopPropagation();
        if (window.confirm("Delete this invoice from history?")) {
            try {
                await deleteDoc(doc(db, "shipment_recent_history", id));
                setRecentInvoices(recentInvoices.filter(rec => rec.id !== id));
            } catch (error) { console.error(error); }
        }
    };

    // Hold/Long Press to Delete
    let pressTimer;
    const handlePressStart = (id) => { pressTimer = setTimeout(() => handleDeleteHistory(id), 800); };
    const handlePressEnd = () => clearTimeout(pressTimer);

    const loadFromHistory = (record) => {
        if(window.confirm("Load this invoice data for editing?")) {
            setReceiver(record.receiver);
            setSenderInfo(record.sender);
            setFooterInfo(record.footer);
            setItems(record.items);
            setSenderMode(record.senderMode || 'internal');
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const totalQuantity = items.reduce((sum, item) => {
        const qtyMatch = item.quantity.toString().match(/(\d+(\.\d+)?)/);
        return sum + (qtyMatch ? parseFloat(qtyMatch[0]) : 0);
    }, 0);
    const grandTotalValue = items.reduce((sum, item) => sum + (item.total || 0), 0);

    return (
        <div className="shipment-invoice-container">
            {/* Fixer Info Box */}
            <div className="no-print" style={{ marginBottom: '15px', padding: '15px', backgroundColor: '#fff', borderLeft: '5px solid #e67e22', borderRadius: '5px', boxShadow: '0 2px 5px rgba(0,0,0,0.05)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                <div>
                    <h4 style={{margin: 0, color: '#e67e22', fontSize: '16px'}}>CN PDF Fixer (For A4 Print)</h4>
                    <p style={{margin: '3px 0 0', fontSize: '13px', color: '#666'}}>Click here to adjust CN PDF layout for A4 page printing.</p>
                </div>
                <button onClick={() => window.open('https://dhl-a4.netlify.app/', '_blank')} style={{ padding: '8px 20px', backgroundColor: '#e67e22', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 'bold', fontSize: '14px', whiteSpace: 'nowrap' }}>
                    <FaTools /> Go to Fixer
                </button>
            </div>

            {/* Control Panel */}
            <div className="no-print control-panel-modern">
                <div className="cp-header">
                    <h3 className="cp-title"><FaFileInvoice /> Shipment Invoice</h3>
                    <p className="cp-subtitle">Prepare Commercial Invoice for Customs</p>
                </div>

                <div className="cp-actions" style={{display: 'block'}}>
                    <div style={{display: 'flex', gap: '15px', marginBottom: '15px', borderBottom: '1px solid #eee', paddingBottom: '10px'}}>
                        <label style={{cursor: 'pointer', fontWeight: senderMode === 'internal' ? 'bold' : 'normal', color: senderMode === 'internal' ? '#2980b9' : '#555'}}>
                            <input type="radio" checked={senderMode === 'internal'} onChange={() => setSenderMode('internal')} style={{marginRight: '5px'}} /> Internal Company
                        </label>
                        <label style={{cursor: 'pointer', fontWeight: senderMode === 'external' ? 'bold' : 'normal', color: senderMode === 'external' ? '#e67e22' : '#555'}}>
                            <input type="radio" checked={senderMode === 'external'} onChange={() => setSenderMode('external')} style={{marginRight: '5px'}} /> External Sender
                        </label>
                    </div>

                    <div className="sender-select-group">
                        <label className="sender-label">{senderMode === 'internal' ? <FaBuilding /> : <FaUserTie />} {senderMode === 'internal' ? ' Sender Company (From Settings)' : ' Manage External Sender'}</label>
                        {senderMode === 'internal' ? (
                            <div className="custom-select-wrapper">
                                <select value={selectedCompanyId} onChange={handleCompanySelect} className="modern-select">
                                    <option value="">Select Sender...</option>
                                    {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                </select>
                            </div>
                        ) : (
                            <div className="external-sender-ui" style={{backgroundColor: '#f9f9f9', padding: '10px', borderRadius: '5px'}}>
                                <div style={{display: 'flex', gap: '5px', marginBottom: '10px'}}>
                                    <select onChange={handleExternalSenderSelect} className="modern-select" style={{flexGrow: 1}}><option value="">-- Load Saved Sender --</option>{savedSenders.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select>
                                </div>
                                <div style={{display: 'grid', gap: '8px'}}>
                                    <input placeholder="Company Name" value={senderInfo.name} onChange={(e) => setSenderInfo({...senderInfo, name: e.target.value})} className="si-input-block" style={{padding: '5px', border: '1px solid #ddd'}} />
                                    <input placeholder="Address" value={senderInfo.address} onChange={(e) => setSenderInfo({...senderInfo, address: e.target.value})} className="si-input-block" style={{padding: '5px', border: '1px solid #ddd'}} />
                                    <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px'}}>
                                        <input placeholder="Contact Person" value={footerInfo.contactPerson} onChange={(e) => setFooterInfo({...footerInfo, contactPerson: e.target.value})} className="si-input-block" style={{padding: '5px', border: '1px solid #ddd'}} />
                                        <input placeholder="Mobile" value={footerInfo.mobile} onChange={(e) => setFooterInfo({...footerInfo, mobile: e.target.value})} className="si-input-block" style={{padding: '5px', border: '1px solid #ddd'}} />
                                    </div>
                                    <input placeholder="Email" value={footerInfo.email} onChange={(e) => setFooterInfo({...footerInfo, email: e.target.value})} className="si-input-block" style={{padding: '5px', border: '1px solid #ddd'}} />
                                </div>
                                <button onClick={handleSaveExternalSender} style={{marginTop: '10px', width: '100%', padding: '6px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px'}}><FaSave /> Save / Add New Sender</button>
                                {savedSenders.length > 0 && (
                                    <div style={{marginTop: '10px', borderTop: '1px solid #ddd', paddingTop: '5px'}}>
                                        <div style={{maxHeight: '80px', overflowY: 'auto', marginTop: '5px'}}>
                                            {savedSenders.map(s => (
                                                <div key={s.id} style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '11px', padding: '4px 5px', borderBottom: '1px dashed #eee', background: '#fff', marginBottom: '2px'}}>
                                                    <span style={{fontWeight: 500}}>{s.name}</span>
                                                    <span onClick={() => handleDeleteSender(s.id, s.name)} style={{ color: '#e74c3c', cursor: 'pointer', padding: '2px 5px', border: '1px solid #eee', borderRadius: '3px', background: '#fff0f0' }}><FaTrash size={10} /></span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <button onClick={handlePrint} className="modern-print-btn" style={{marginTop: '15px'}}><FaPrint /> Print Invoice</button>
                </div>
            </div>

            {/* A4 Invoice Sheet */}
            <div id="shipment-invoice-content" className="invoice-paper-a4">
                <div className="si-header-compact">
                    <h1 className="si-header-title">{senderInfo.name || 'COMPANY NAME'}</h1>
                    <p className="si-header-address">{senderInfo.address || 'COMPANY ADDRESS WILL APPEAR HERE'}</p>
                </div>

                <div className="si-buyer-section">
                    <div className="si-aligned-row"><span className="si-label-fixed">DATE</span><span className="si-colon">:</span><input className="si-input" value={receiver.date} onChange={(e) => setReceiver({...receiver, date: e.target.value})} /></div>
                    <div className="si-aligned-row" style={{position: 'relative'}}>
                        <span className="si-label-fixed">TO</span><span className="si-colon">:</span>
                        <div style={{display: 'flex', flexGrow: 1, alignItems: 'center', gap: '5px'}}><input className="si-input-block bold-text" placeholder="BUYER COMPANY NAME" value={receiver.toCompany} onChange={(e) => setReceiver({...receiver, toCompany: e.target.value})} /><button className="no-print suggestion-btn" onClick={() => setShowBuyerDropdown(!showBuyerDropdown)} title="Saved Buyers"><FaList /></button></div>
                        {showBuyerDropdown && (
                            <div className="buyer-dropdown no-print">
                                <div className="dropdown-header">Saved Buyers <span style={{float:'right', cursor:'pointer'}} onClick={()=>setShowBuyerDropdown(false)}><FaTimes/></span></div>
                                <ul>{savedBuyers.map(buyer => (<li key={buyer.id}><span className="buyer-name" onClick={() => selectBuyer(buyer)}>{buyer.name}</span><span className="delete-icon" onClick={() => handleDeleteBuyer(buyer.id, buyer.name)}><FaTrash /></span></li>))}</ul>
                            </div>
                        )}
                    </div>
                    <div className="si-aligned-row align-top"><span className="si-label-fixed">ADDRESS</span><span className="si-colon">:</span><textarea className="si-textarea" placeholder="Buyer Address..." value={receiver.address} onChange={(e) => setReceiver({...receiver, address: e.target.value})} /></div>
                    <div className="si-aligned-row"><span className="si-label-fixed">ATTN</span><span className="si-colon">:</span><input className="si-input" placeholder="Contact Person" value={receiver.attn} onChange={(e) => setReceiver({...receiver, attn: e.target.value})} /></div>
                    <div className="si-aligned-row"><span className="si-label-fixed">PHONE</span><span className="si-colon">:</span><input className="si-input" placeholder="Phone Number" value={receiver.phone} onChange={(e) => setReceiver({...receiver, phone: e.target.value})} /></div>
                    <div className={`si-aligned-row ${(!receiver.vat) ? 'hide-on-print' : ''}`}><span className="si-label-fixed">VAT</span><span className="si-colon">:</span><input className="si-input" placeholder="VAT Number" value={receiver.vat} onChange={e=>setReceiver({...receiver, vat: e.target.value})} /></div>
                    <div className={`si-aligned-row ${(!receiver.eori) ? 'hide-on-print' : ''}`}><span className="si-label-fixed">EORI</span><span className="si-colon">:</span><input className="si-input" placeholder="EORI Number" value={receiver.eori} onChange={e=>setReceiver({...receiver, eori: e.target.value})} /></div>
                    <div className={`si-aligned-row ${(!receiver.tax) ? 'hide-on-print' : ''}`}><span className="si-label-fixed">TAX</span><span className="si-colon">:</span><input className="si-input" placeholder="TAX Number" value={receiver.tax} onChange={e=>setReceiver({...receiver, tax: e.target.value})} /></div>
                </div>

                <div className="table-responsive">
                    <table className="si-table">
                        <thead>
                            <tr>
                                <th style={{width: '14%'}}>ORIGIN</th>
                                <th style={{width: '15%'}}>FABRIC QUALITY</th>
                                <th style={{width: '10%'}}>QTY/UNIT</th>
                                <th style={{width: '23%'}}>DESCRIPTION OF GOODS</th>
                                <th style={{width: '12%'}}>HS CODE</th>
                                <th style={{width: '12%'}}>UNIT PRICE ($)</th>
                                <th style={{width: '14%'}}>TOTAL VALUE ($)</th>
                                <th className="no-print" style={{width: '4%'}}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item, index) => (
                                <tr key={index}>
                                    <td><input className="si-table-input text-center" value={item.origin} readOnly style={{fontWeight:'bold'}} /></td>
                                    <td><input className="si-table-input text-center" list="fabricList" data-row={index} data-col="1" onKeyDown={(e) => handleKeyDown(e, index, 1)} value={item.fabric} onChange={(e) => handleItemChange(index, 'fabric', e.target.value)} /></td>
                                    <td><input type="text" className="si-table-input text-center" data-row={index} data-col="2" onKeyDown={(e) => handleKeyDown(e, index, 2)} value={item.quantity} onChange={(e) => handleItemChange(index, 'quantity', e.target.value)} placeholder="0" /></td>
                                    <td><input className="si-table-input" list="productList" data-row={index} data-col="3" onKeyDown={(e) => handleKeyDown(e, index, 3)} value={item.description} onChange={(e) => handleItemChange(index, 'description', e.target.value)} /></td>
                                    <td><input className="si-table-input text-center" data-row={index} data-col="4" onKeyDown={(e) => handleKeyDown(e, index, 4)} value={item.hsCode} onChange={(e) => handleItemChange(index, 'hsCode', e.target.value)} /></td>
                                    {/* ✅ ✅ ✅ ✅ UNIT PRICE ($) WITH 0.00 FORMAT ON BLUR ✅ ✅ ✅ ✅ */}
                                    <td style={{position: 'relative'}}>
                                        <span className="no-print" style={{position:'absolute', left:'4px', top:'50%', transform:'translateY(-50%)', fontSize:'11px', color:'#000000'}}>$</span>
                                        <input 
                                            type="number" 
                                            className="si-table-input text-right" 
                                            data-row={index} data-col="5"
                                            onKeyDown={(e) => handleKeyDown(e, index, 5)}
                                            placeholder="0.00"
                                            value={item.unitPrice} 
                                            onChange={(e) => handleItemChange(index, 'unitPrice', e.target.value)}
                                            onBlur={() => {
                                                const val = parseFloat(items[index].unitPrice);
                                                if (!isNaN(val)) handleItemChange(index, 'unitPrice', val.toFixed(2));
                                            }}
                                            style={{paddingLeft: '12px'}}
                                        />
                                    </td>
                                    <td className="text-right bold-text">${item.total.toFixed(2)}</td>
                                    <td className="no-print text-center">{items.length > 1 && (<button onClick={() => removeItem(index)} className="delete-btn-small"><FaTrash /></button>)}</td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr>
                                <td colSpan="2" style={{textAlign: 'right', fontWeight: 'bold'}}>TOTAL </td>
                                <td style={{textAlign: 'center', fontWeight: 'bold'}}>{totalQuantity}</td>
                                <td colSpan="3" style={{textAlign: 'right', fontWeight: 'bold'}}>TOTAL VALUE </td>
                                <td style={{textAlign: 'right', fontWeight: 'bold'}}>${grandTotalValue.toFixed(2)}</td>
                                <td className="no-print"><button onClick={addItem} className="add-btn-small"><FaPlus /></button></td>
                            </tr>
                        </tfoot>
                    </table>
                </div>

                <datalist id="fabricList">{fabricSuggestions.map((f, i) => <option key={i} value={f} />)}</datalist>
                <datalist id="productList">{productSuggestions.map((p, i) => <option key={i} value={p} />)}</datalist>

                <div className="si-disclaimer-box"><p>SAMPLES WITHOUT COMMERCIAL VALUE</p><p>VALUE FOR CUSTOMS PURPOSE ONLY</p></div>

                <div className="si-footer">
                    <p style={{marginBottom: '5px'}}>WITH WARM REGARDS</p>
                    <p className="si-footer-company bold-text" style={{fontSize: '15px', margin: '5px 0', textTransform: 'uppercase'}}>{senderInfo.name || 'COMPANY NAME'}</p>
                    <input className="si-input-footer bold-text" style={{width: '100%', marginBottom: '2px', textTransform: 'uppercase'}} value={footerInfo.contactPerson} onChange={(e) => setFooterInfo({...footerInfo, contactPerson: e.target.value})} placeholder="CONTACT PERSON" />
                    <div style={{marginTop: '0px'}}>
                        <div style={{display: 'flex', alignItems: 'center', marginBottom: '0px'}}><span style={{width:'40px', fontSize: '12px'}}>Mobile:</span><input className="si-input-footer" value={footerInfo.mobile} onChange={(e) => setFooterInfo({...footerInfo, mobile: e.target.value})} /></div>
                        <div style={{display: 'flex', alignItems: 'center'}}><span style={{width:'40px', fontSize: '12px'}}>Email:</span><input className="si-input-footer" value={footerInfo.email} onChange={(e) => setFooterInfo({...footerInfo, email: e.target.value})} /></div>
                    </div>
                </div>

                {/* ✅ Firebase Recent Invoices History Section */}
                <div className="no-print" style={{marginTop: '40px', paddingTop: '20px', borderTop: '2px dashed #ddd'}}>
                    <h3 style={{display: 'flex', alignItems: 'center', gap: '10px', color: '#2c3e50'}}><FaHistory /> Recent Invoices </h3>
                    <p style={{fontSize: '12px', color: '#7f8c8d', marginBottom: '15px'}}>Click to Edit. Hold (Long Press) to Delete.</p>
                    <div style={{display: 'grid', gap: '10px'}}>
                        {(showAllRecent ? recentInvoices : recentInvoices.slice(0, 3)).map((record) => (
                            <div key={record.id} onClick={() => loadFromHistory(record)} onMouseDown={() => handlePressStart(record.id)} onMouseUp={handlePressEnd} onTouchStart={() => handlePressStart(record.id)} onTouchEnd={handlePressEnd} style={{ padding: '12px', background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', transition: 'all 0.2s', userSelect: 'none' }}>
                                <div style={{flexGrow: 1}}>
                                    <div style={{display: 'flex', gap: '15px', marginBottom: '4px'}}><span style={{fontWeight: 'bold', fontSize: '14px', color: '#2980b9'}}>{record.receiver.toCompany || "No Name"}</span><span style={{fontSize: '12px', color: '#95a5a6'}}>{record.timestamp}</span></div>
                                    <div style={{fontSize: '12px', color: '#34495e'}}><strong>Items:</strong> {record.summary}</div>
                                </div>
                                <div style={{display: 'flex', gap: '10px'}}><FaEdit style={{color: '#3498db', opacity: 0.6}} /><FaTrash onClick={(e) => handleDeleteHistory(record.id, e)} style={{color: '#f13a25', opacity: 0.5}} /></div>
                            </div>
                        ))}
                    </div>
                    {recentInvoices.length > 3 && (
                        <button onClick={() => setShowAllRecent(!showAllRecent)} style={{ marginTop: '15px', width: '100%', padding: '8px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer', color: '#555', fontWeight: '500' }}>{showAllRecent ? "Show Less" : "See More Recent Invoices"}</button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ShipmentInvoice;